// Copyright (c) Docugami, Inc. All rights reserved.

import emptyDocumentsCoachMarkUrl from "assets/EmptyDocuments.png";
import { InfoView } from "components/work_path/InfoView";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import styled from "styled-components";
import { usePageLimits } from "./PageLimits";
import { useAppSelector } from "./redux";

const Container = styled.div<{overflow?: string, onClick?: any}>`
    width: 100%;
    height: 100%;
    background: #FFFFFFBF;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    user-select: none;
    overflow-y: ${props => props.overflow ?? 'auto'};
    ${props => !!props.onClick && "cursor: pointer"};
    img {
        pointer-events: none;
    }
`

export function useFileViewCoachMarks(isRoot: boolean, onClick: () => void, fileCount?: number): JSX.Element | null {
    const { pageUploadDisabled } = usePageLimits();

    if (fileCount === 0 && isRoot) {
        // Show empty files coach mark if there are no files in the root directory
        if (pageUploadDisabled) {
            return <UploadDisabledCoachMark onClick={onClick} />
        }
        return <EmptyDocumentsCoachMark onClick={onClick} />
    }

    return null;
}

const Image = styled.img<{size?: number}>`
    width: ${props => props.size ? `${props.size}px` : '250px'};
`

const EmptyDocumentsCoachMark = (props: {onClick: () => void}): JSX.Element => {
    const text = 'Looks like you could use some documents!'
    const subtext = 'Click here to get started with Docugami.  We currently support .docx, .doc and .pdf files.'
    return (
        <Container onClick={props.onClick} >
            <InfoView
                icon={<Image src={emptyDocumentsCoachMarkUrl} />}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    );
}

const UploadDisabledCoachMark = (props: {onClick: () => void}): JSX.Element => {
    const text = 'Looks like you could use some documents!'
    const subtext = 'Click here to upgrade so you can start adding documents.'
    return (
        <Container onClick={props.onClick} >
            <InfoView
                icon={<Image src={emptyDocumentsCoachMarkUrl} />}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    );
}

export const EmptyDocumentsAddDocsCoachMark = (): JSX.Element => {
    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles);

    const text = 'Looks like you could use some documents!'
    const subtext = `Upload ${docSetDocThreshold === undefined ? 'some' : `at least ${docSetDocThreshold}`} or more similar docs to create a DOC SET, which you can use to start a project.\n\n Choose a source below to get started.`
    return (
        <Container overflow={'visible'}>
            <InfoView
                icon={<Image src={emptyDocumentsCoachMarkUrl} size={180} />}
                titleText={text}
                secondaryText={[subtext]} />
        </Container>
    );
}
